import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Building2, CalendarDays, Settings, Plus, User, Car, LogOut, Star, Wallet, Hotel, QrCode, HeartPulse, ChevronDown, Compass, BookOpen, Share2 } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useMarketing } from '../contexts/MarketingContext';
import { t } from '../utils/translations';
import LanguageSelector from './LanguageSelector';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useCanCollectPayments } from '../hooks/useCanCollectPayments';
import { ADMIN_PANEL_PATH } from '../config/adminRoutes';
import './ModernHeader.css';

const ModernHeader = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isDriver, setIsDriver] = useState(false);
  const [openDesktopGroup, setOpenDesktopGroup] = useState(null);
  const desktopCloseTimer = useRef(null);
  const navRef = useRef(null);
  const location = useLocation();
  const { language } = useLanguage();
  const { currentUser, userProfile, logout, getRoles, isAdmin, isCommercialAgentOnly, isCommercialAgent } = useAuth();
  const { marketingStats } = useMarketing();
  const { canCollect } = useCanCollectPayments();
  const navigate = useNavigate();

  const handleRegisterBusiness = () => {
    if (!currentUser) {
      navigate('/login', {
        state: {
          from: { pathname: '/register-business' },
          registerBusiness: true
        }
      });
    } else {
      navigate('/register-business');
    }
  };

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (!(isMobile && isMenuOpen)) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isMobile, isMenuOpen]);

  useEffect(() => {
    closeMenu();
    setOpenDesktopGroup(null);
  }, [location.pathname]);

  useEffect(() => {
    if (!openDesktopGroup) return undefined;
    const onPointerDown = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setOpenDesktopGroup(null);
      }
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setOpenDesktopGroup(null);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [openDesktopGroup]);

  // Vérifier si l'utilisateur est un chauffeur
  useEffect(() => {
    const checkIfDriver = async () => {
      if (!currentUser) {
        setIsDriver(false);
        return;
      }

      try {
        const taxiQ = query(
          collection(db, 'taxiRequests'),
          where('status', '==', 'approved')
        );
        const taxiSnapshot = await getDocs(taxiQ);
        let driverFound = false;
        taxiSnapshot.forEach((docSnap) => {
          const data = docSnap.data();
          if ((data.userId && data.userId === currentUser.uid) ||
              (data.email && data.email.toLowerCase() === currentUser.email?.toLowerCase())) {
            driverFound = true;
          }
        });
        setIsDriver(driverFound);
      } catch (error) {
        console.error('Erreur lors de la vérification du taxi:', error);
        setIsDriver(false);
      }
    };

    checkIfDriver();
  }, [currentUser]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
      closeMenu();
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    }
  };

  const getDashboardPath = () => {
    const roles = getRoles();

    // Agent commercial sans autre gestion → espace agent
    if (isCommercialAgentOnly()) {
      return '/dashboard-agent';
    }

    if (roles.length > 1) {
      return '/select-dashboard';
    }
    
    // Si un seul rôle, rediriger directement
    if (roles.includes('admin')) return ADMIN_PANEL_PATH;
    if (roles.includes('driver') || roles.includes('chauffeur')) return '/dashboard-driver';
    if (roles.includes('rental') || roles.includes('rental_owner') || roles.includes('loueur')) return '/dashboard-rental';
    if (roles.includes('business') || roles.includes('businessOwner') || roles.includes('owner')) return '/dashboard-business';
    if (roles.includes('cash_agent')) return '/dashboard-cash-agent';
    if (roles.includes('commercial_agent') || isCommercialAgent()) return '/dashboard-agent';
    return '/dashboard-client';
  };
  
  const getDashboardLabel = () => {
    const roles = getRoles();
    if (isCommercialAgentOnly()) {
      return t(language, 'header.commercialAgent');
    }
    if (roles.length > 1) {
      return t(language, 'header.selectDashboard');
    }
    if (roles.includes('admin')) {
      return t(language, 'header.admin');
    }
    if (roles.includes('driver') || roles.includes('chauffeur')) {
      return t(language, 'header.driver');
    }
    if (roles.includes('rental') || roles.includes('rental_owner') || roles.includes('loueur')) {
      return t(language, 'header.rental');
    }
    if (roles.includes('commercial_agent') || isCommercialAgent()) {
      return t(language, 'header.commercialAgent');
    }
    return t(language, 'header.myAccount');
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const servicesItems = [
    { path: '/ecosystem/portefeuille', labelKey: 'header.wallet', icon: Wallet },
    ...(canCollect
      ? [{ path: '/ecosystem/encaisser', labelKey: 'header.collect', icon: QrCode }]
      : []),
    { path: '/bookings', labelKey: 'header.bookings', icon: CalendarDays },
    { path: '/events', labelKey: 'header.events', icon: CalendarDays }
  ];

  const menuGroups = [
    {
      id: 'explore',
      labelKey: 'header.exploreGroup',
      icon: Compass,
      items: [
        { path: '/', labelKey: 'header.home', logoIcon: true },
        { path: '/category/hotels', labelKey: 'header.hotels', icon: Hotel },
        { path: '/all-categories', labelKey: 'header.businesses', icon: Building2 }
      ]
    },
    {
      id: 'services',
      labelKey: 'header.servicesGroup',
      icon: Wallet,
      items: servicesItems
    }
  ];

  const guideLink = {
    path: '/vivre-en-guinee',
    labelKey: 'header.livingInGuinea',
    flagIcon: true
  };

  const isPathActive = (path) => location.pathname === path;
  const isGroupActive = (group) => group.items.some((item) => isPathActive(item.path));

  const clearDesktopCloseTimer = () => {
    if (desktopCloseTimer.current) {
      clearTimeout(desktopCloseTimer.current);
      desktopCloseTimer.current = null;
    }
  };

  const openDesktopMenu = (groupId) => {
    clearDesktopCloseTimer();
    setOpenDesktopGroup(groupId);
  };

  const scheduleDesktopClose = () => {
    clearDesktopCloseTimer();
    desktopCloseTimer.current = setTimeout(() => {
      setOpenDesktopGroup(null);
    }, 180);
  };

  const toggleDesktopMenu = (groupId) => {
    clearDesktopCloseTimer();
    setOpenDesktopGroup((prev) => (prev === groupId ? null : groupId));
  };

  const renderNavIcon = (item, size) => {
    if (item.logoIcon) {
      return (
        <img
          src="/logo.png"
          alt={t(language, 'header.logoAlt')}
          style={{
            width: size,
            height: size,
            objectFit: 'contain',
            borderRadius: '6px'
          }}
        />
      );
    }
    if (item.flagIcon) {
      return (
        <img
          src="/guinee.png"
          alt={t(language, 'header.flagAlt')}
          style={{
            width: size,
            height: size,
            objectFit: 'cover',
            borderRadius: '4px'
          }}
        />
      );
    }

    const Icon = item.icon;
    return <Icon size={size} />;
  };

  const headerKey = isMobile ? 'mobile' : 'desktop';

  return (
    <header style={{
      backgroundColor: 'rgba(255,255,255,0.94)',
      backdropFilter: isMenuOpen ? 'none' : 'blur(16px)',
      WebkitBackdropFilter: isMenuOpen ? 'none' : 'blur(16px)',
      boxShadow: '0 10px 30px rgba(11,28,23,0.08)',
      position: 'sticky',
      top: 0,
      zIndex: 1000,
      borderBottom: '1px solid rgba(12, 61, 114, 0.08)'
    }}>
      <div key={headerKey}>
      {/* Barre supérieure avec bouton de connexion */}
      {!isMobile && (
        <div style={{
          background: 'linear-gradient(180deg, rgba(12,61,114,0.04), rgba(12,61,114,0.01))',
          borderBottom: '1px solid rgba(12, 61, 114, 0.08)',
          padding: '0.5rem 0'
        }}>
          <div style={{
            maxWidth: '1280px',
            margin: '0 auto',
            padding: '0 1.5rem',
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: '1rem'
          }}>
            <Link
              to="/allo"
              style={{
                color: '#ffffff',
                textDecoration: 'none',
                fontWeight: '600',
                fontSize: '0.875rem',
                padding: '0.5rem 1.25rem',
                borderRadius: '999px',
                background: 'rgba(12, 61, 114, 0.86)',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                boxShadow: '0 6px 16px rgba(12, 61, 114, 0.35)',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 8px 20px rgba(12, 61, 114, 0.45)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(12, 61, 114, 0.35)';
              }}
            >
              <Car size={16} />
              {t(language, 'header.callTaxi')}
            </Link>
            {!currentUser ? (
              <Link
                to="/login"
                onClick={(e) => {
                  // Garantir la navigation même si un overlay intercepte le Link
                  e.preventDefault();
                  navigate('/login');
                }}
                style={{
                  color: '#0c3d72',
                  textDecoration: 'none',
                  fontWeight: '600',
                  fontSize: '0.875rem',
                  padding: '0.5rem 1.25rem',
                  borderRadius: '6px',
                  border: '1px solid #0c3d72',
                  backgroundColor: '#ffffff',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                  position: 'relative',
                  zIndex: 2,
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#0c3d72';
                  e.currentTarget.style.color = 'white';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(12, 61, 114, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#ffffff';
                  e.currentTarget.style.color = '#0c3d72';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 1px 2px rgba(0,0,0,0.05)';
                }}
              >
                <User size={16} />
                {t(language, 'header.login')}
              </Link>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <Link
                  to="/profile"
                  style={{
                    color: '#6b7280',
                    textDecoration: 'none',
                    fontWeight: '500',
                    fontSize: '0.875rem',
                    padding: '0.5rem 1rem',
                    borderRadius: '6px',
                    backgroundColor: '#ffffff',
                    border: '1px solid #e5e7eb',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#eef5f1';
                    e.currentTarget.style.color = '#0c3d72';
                    e.currentTarget.style.borderColor = '#0c3d72';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#ffffff';
                    e.currentTarget.style.color = '#6b7280';
                    e.currentTarget.style.borderColor = '#e5e7eb';
                  }}
                >
                  <User size={16} />
                  {t(language, 'header.myProfile')}
                </Link>
                <Link
                  to={getDashboardPath()}
                  style={{
                    color: '#6b7280',
                    textDecoration: 'none',
                    fontWeight: '500',
                    fontSize: '0.875rem',
                    padding: '0.5rem 1rem',
                    borderRadius: '6px',
                    backgroundColor: '#ffffff',
                    border: '1px solid #e5e7eb',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#eef5f1';
                    e.currentTarget.style.color = '#0c3d72';
                    e.currentTarget.style.borderColor = '#0c3d72';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#ffffff';
                    e.currentTarget.style.color = '#6b7280';
                    e.currentTarget.style.borderColor = '#e5e7eb';
                  }}
                >
                  {(() => {
                    const roles = getRoles();
                    if (roles.includes('admin')) return <Settings size={16} />;
                    if (roles.includes('driver') || roles.includes('chauffeur')) return <Car size={16} />;
                    if (roles.includes('commercial_agent') || isCommercialAgent()) return <Share2 size={16} />;
                    return <User size={16} />;
                  })()}
                  {getDashboardLabel()}
                </Link>
                <button
                  onClick={handleLogout}
                  style={{
                    color: '#ef4444',
                    textDecoration: 'none',
                    fontWeight: '500',
                    fontSize: '0.875rem',
                    padding: '0.5rem 1rem',
                    borderRadius: '6px',
                    border: '1px solid #fee2e2',
                    backgroundColor: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    transition: 'all 0.2s ease',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#fee2e2';
                    e.currentTarget.style.borderColor = '#fecaca';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#ffffff';
                    e.currentTarget.style.borderColor = '#fee2e2';
                  }}
                >
                  <LogOut size={16} />
                  {t(language, 'header.logout')}
                </button>
              </div>
            )}
            {currentUser && (
              <Link
                to="/marketing"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                  padding: '0.35rem 0.75rem',
                  borderRadius: '999px',
                  background: 'rgba(12, 61, 114, 0.86)',
                  color: 'white',
                  fontSize: '0.75rem',
                  fontWeight: '700',
                  textDecoration: 'none',
                  boxShadow: '0 6px 16px rgba(12, 61, 114, 0.35)'
                }}
                aria-label={t(language, 'header.viewRewards')}
              >
                <Star size={14} />
                {t(language, 'header.level')} {marketingStats.level || 1}
              </Link>
            )}
            <LanguageSelector />
          </div>
        </div>
      )}

      {/* Menu principal */}
      <div style={{
        maxWidth: '1280px',
        margin: '0 auto',
        padding: isMobile ? '0 1rem' : '0 1.5rem',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        height: isMobile ? '56px' : '70px',
        gap: 0
      }}>
        {/* Logo */}
        <Link 
          to="/" 
          onClick={closeMenu}
          style={{
            display: 'flex',
            alignItems: 'center',
            textDecoration: 'none',
            height: isMobile ? '40px' : '60px',
            backgroundColor: 'white',
            padding: isMobile ? '0.2rem' : '0.5rem',
            borderRadius: '8px',
            gap: '0.75rem'
          }}
        >
          <img 
            src="/logo.png" 
            alt="Conakry en Poche" 
            style={{
              height: isMobile ? '36px' : '60px',
              width: 'auto',
              objectFit: 'contain'
            }}
          />
          {!isMobile && (
            <span style={{
              fontSize: '0.9rem',
              fontWeight: '600',
              color: '#1f2937'
            }}>
              conakryenpoche.com
            </span>
          )}
        </Link>

        {/* Desktop Navigation */}
        {!isMobile && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem' }}>
          <Link
            to="/ecosystem/sante"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.65rem 1rem',
              borderRadius: '999px',
                  background: 'rgba(12, 61, 114, 0.86)',
              color: '#ffffff',
              textDecoration: 'none',
              fontWeight: '800',
              boxShadow: 'none'
            }}
          >
            <HeartPulse size={18} />
            {t(language, 'header.health')}
          </Link>
          <nav
            ref={navRef}
            className="mh-nav"
            style={{
              display: 'flex',
              gap: '0.65rem',
              alignItems: 'center'
            }}
          >
            {menuGroups.map((group) => {
              const GroupIcon = group.icon;
              const active = isGroupActive(group);
              const isOpen = openDesktopGroup === group.id;
              return (
                <div
                  key={group.id}
                  className={`mh-group${isOpen ? ' is-open' : ''}`}
                  onMouseEnter={() => openDesktopMenu(group.id)}
                  onMouseLeave={scheduleDesktopClose}
                >
                  <button
                    type="button"
                    className="mh-group__trigger"
                    aria-expanded={isOpen}
                    aria-haspopup="menu"
                    onClick={() => toggleDesktopMenu(group.id)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.45rem',
                      border: '1px solid rgba(12, 61, 114, 0.12)',
                      background: active || isOpen
                        ? 'rgba(12, 61, 114, 0.08)'
                        : '#ffffff',
                      color: active || isOpen ? '#0c3d72' : '#4b5563',
                      borderRadius: '999px',
                      padding: '0.65rem 0.95rem',
                      minHeight: 44,
                      fontWeight: '700',
                      fontSize: '0.9rem',
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      boxShadow: isOpen ? '0 10px 22px rgba(12,61,114,0.1)' : 'none'
                    }}
                  >
                    <GroupIcon size={16} />
                    {t(language, group.labelKey)}
                    <ChevronDown
                      size={16}
                      style={{
                        transform: isOpen ? 'rotate(180deg)' : 'none',
                        transition: 'transform 0.18s ease'
                      }}
                    />
                  </button>
                  {isOpen && (
                    <div
                      className="mh-flyout"
                      role="menu"
                      onMouseEnter={() => openDesktopMenu(group.id)}
                      onMouseLeave={scheduleDesktopClose}
                    >
                      <div className="mh-flyout__panel">
                        {group.items.map((item) => (
                          <Link
                            key={item.path}
                            to={item.path}
                            role="menuitem"
                            className={`mh-flyout__link${isPathActive(item.path) ? ' is-active' : ''}`}
                            onClick={() => setOpenDesktopGroup(null)}
                          >
                            {renderNavIcon(item, 18)}
                            {t(language, item.labelKey)}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            <Link
              to={guideLink.path}
              className={`mh-direct${isPathActive(guideLink.path) ? ' is-active' : ''}`}
            >
              {renderNavIcon(guideLink, 16)}
              {t(language, guideLink.labelKey)}
            </Link>
          </nav>
          </div>
        )}

        {/* Mobile Menu Button et Actions */}
        {isMobile && (
          <div style={{
            marginLeft: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: '0.35rem'
          }}>
            {!currentUser ? (
              <Link
                to="/login"
                aria-label={t(language, 'header.login')}
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/login');
                }}
                style={{
                  color: '#0c3d72',
                  textDecoration: 'none',
                  padding: '0.45rem',
                  borderRadius: '999px',
                  border: '1px solid rgba(12, 61, 114, 0.14)',
                  backgroundColor: '#ffffff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                  zIndex: 2,
                  cursor: 'pointer'
                }}
              >
                <User size={18} />
              </Link>
            ) : (
              <Link
                to="/profile"
                aria-label={t(language, 'header.myProfile')}
                style={{
                  color: '#0c3d72',
                  textDecoration: 'none',
                  padding: '0.45rem',
                  borderRadius: '999px',
                  backgroundColor: '#ffffff',
                  border: '1px solid rgba(12, 61, 114, 0.14)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <User size={18} />
              </Link>
            )}
            <LanguageSelector compact />
            <button
              onClick={toggleMenu}
              style={{
                background: '#ffffff',
                border: '1px solid rgba(12, 61, 114, 0.14)',
                cursor: 'pointer',
                width: 44,
                height: 44,
                borderRadius: '999px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#0c3d72'
              }}
              aria-label={t(language, 'header.toggleMenu')}
              aria-expanded={isMenuOpen}
            >
              {isMenuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        )}
      </div>

      {/* Mobile Menu — portaled to body to avoid sticky/backdrop-filter clipping */}
      {isMobile && isMenuOpen && createPortal(
        <>
          <div
            onClick={closeMenu}
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(0,0,0,0.42)',
              zIndex: 10040
            }}
            aria-hidden="true"
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-label={t(language, 'header.toggleMenu')}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: '#ffffff',
              zIndex: 10050,
              overflowY: 'auto',
              WebkitOverflowScrolling: 'touch',
              padding: 'calc(env(safe-area-inset-top, 0px) + 4.25rem) 1rem calc(env(safe-area-inset-bottom, 0px) + 6rem)',
              boxShadow: '0 18px 40px rgba(11,28,23,0.12)'
            }}
          >
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              zIndex: 10051,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '0.75rem',
              padding: 'calc(env(safe-area-inset-top, 0px) + 0.65rem) 1rem 0.65rem',
              background: 'rgba(255,255,255,0.96)',
              borderBottom: '1px solid rgba(12, 61, 114, 0.08)'
            }}>
              <strong style={{ color: '#0c3d72', fontSize: '1rem' }}>
                {language === 'fr' ? 'Menu' : 'Menu'}
              </strong>
              <button
                type="button"
                onClick={closeMenu}
                aria-label={t(language, 'header.toggleMenu')}
                style={{
                  background: '#ffffff',
                  border: '1px solid rgba(12, 61, 114, 0.14)',
                  borderRadius: '999px',
                  width: 40,
                  height: 40,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#0c3d72',
                  cursor: 'pointer'
                }}
              >
                <X size={22} />
              </button>
            </div>

            <div style={{
              marginBottom: '1rem',
              padding: '1rem',
              borderRadius: '16px',
                  background: 'rgba(12, 61, 114, 0.86)',
              color: '#ffffff',
              boxShadow: '0 14px 32px rgba(12,61,114,0.22)'
            }}>
              <div style={{ fontSize: '0.72rem', letterSpacing: '0.08em', textTransform: 'uppercase', opacity: 0.78, marginBottom: '0.35rem' }}>
                Conakry en Poche
              </div>
              <strong style={{ display: 'block', fontSize: '1rem', marginBottom: '0.3rem' }}>
                {language === 'fr' ? 'Navigation' : 'Navigation'}
              </strong>
              <p style={{ margin: 0, fontSize: '0.9rem', lineHeight: 1.45, color: 'rgba(255,255,255,0.84)' }}>
                {language === 'fr'
                  ? 'Accédez rapidement aux adresses, services et infos pratiques.'
                  : 'Quickly reach places, services and practical info.'}
              </p>
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '0.65rem',
              marginBottom: '1.25rem'
            }}>
              <Link
                to="/allo"
                onClick={closeMenu}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.4rem',
                  background: 'rgba(12, 61, 114, 0.86)',
                  color: '#ffffff',
                  padding: '0.85rem 0.75rem',
                  fontWeight: '700',
                  textDecoration: 'none',
                  fontSize: '0.9rem'
                }}
              >
                <Car size={18} />
                {t(language, 'header.callTaxi')}
              </Link>
              <button
                onClick={() => {
                  closeMenu();
                  handleRegisterBusiness();
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.4rem',
                  background: 'rgba(12, 61, 114, 0.86)',
                  color: '#ffffff',
                  padding: '0.85rem 0.75rem',
                  fontWeight: '700',
                  fontSize: '0.9rem',
                  border: 'none',
                  cursor: 'pointer'
                }}
              >
                <Plus size={18} />
                {t(language, 'header.register')}
              </button>
              <Link
                to="/ecosystem/sante"
                onClick={closeMenu}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.4rem',
                  background: 'rgba(12, 61, 114, 0.86)',
                  color: '#ffffff',
                  padding: '0.85rem 0.75rem',
                  fontWeight: '800',
                  textDecoration: 'none',
                  fontSize: '0.9rem',
                  border: '1px solid #ffffff',
                  gridColumn: '1 / -1'
                }}
              >
                <HeartPulse size={18} />
                {t(language, 'header.health')}
              </Link>
            </div>
            <nav className="mh-mobile-nav">
              {[...menuGroups, { id: 'guide', labelKey: 'header.guideGroup', icon: BookOpen, items: [guideLink] }].map((group) => {
                const GroupIcon = group.icon;
                return (
                  <section key={group.id} className="mh-mobile-section">
                    <h3 className="mh-mobile-section__title">
                      <GroupIcon size={16} />
                      {t(language, group.labelKey)}
                    </h3>
                    <div className="mh-mobile-section__list">
                      {group.items.map((item) => (
                        <Link
                          key={item.path}
                          to={item.path}
                          onClick={closeMenu}
                          className={`mh-mobile-link${isPathActive(item.path) ? ' is-active' : ''}`}
                        >
                          {renderNavIcon(item, 18)}
                          {t(language, item.labelKey)}
                        </Link>
                      ))}
                    </div>
                  </section>
                );
              })}
              {currentUser && (
                <>
                  <div style={{ height: '1px', backgroundColor: '#e5e7eb', margin: '0.75rem 0' }} />
                  <Link
                    to="/profile"
                    onClick={closeMenu}
                    className="mh-mobile-link"
                  >
                    <User size={20} />
                    {t(language, 'header.myProfile')}
                  </Link>
                  <Link
                    to={getDashboardPath()}
                    onClick={closeMenu}
                    className="mh-mobile-link"
                  >
                    {isDriver ? <Car size={20} /> : <User size={20} />}
                    {isDriver
                      ? t(language, 'header.driverDashboard')
                      : t(language, 'header.myDashboard')}
                  </Link>
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="mh-mobile-link mh-mobile-link--danger"
                  >
                    <LogOut size={20} />
                    {t(language, 'header.logout')}
                  </button>
                </>
              )}
              {!currentUser && (
                <Link
                  to="/login"
                  onClick={(e) => {
                    e.preventDefault();
                    closeMenu();
                    navigate('/login');
                  }}
                  className="mh-mobile-link mh-mobile-link--outline"
                >
                  <User size={20} />
                  {t(language, 'header.login')}
                </Link>
              )}
              {isAdmin() && (
                <Link
                  to={ADMIN_PANEL_PATH}
                  onClick={closeMenu}
                  className="mh-mobile-link"
                >
                  <Settings size={20} />
                  {t(language, 'header.administration')}
                </Link>
              )}
            </nav>
          </div>
        </>,
        document.body
      )}
      </div>
    </header>
  );
};

export default ModernHeader;
